import { formatReportDateShort } from '../../utils/cropCalendarReport';
import {
  chartYTicks,
  clearPoints,
  formatCloudOutlierValue,
  formatFieldHealthValue,
  type SeriesPoint,
} from '../../utils/fieldHealthReportModel';

export function Sparkline({ points, color, outliers }: { points: SeriesPoint[]; color: string; outliers: string[] }) {
  const outlierSet = new Set(outliers);
  const clear = clearPoints(points, outlierSet);
  if (clear.length < 2) return null;
  const w = 120;
  const h = 32;
  const min = Math.min(...clear.map((p) => p.value));
  const max = Math.max(...clear.map((p) => p.value));
  const range = max - min || 1;
  const coords = clear
    .map((p, i) => {
      const x = 6 + (i / (clear.length - 1)) * (w - 12);
      const y = h - 6 - ((p.value - min) / range) * (h - 12);
      return `${x},${y}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} width="100%" className="my-1.5" aria-hidden>
      <polyline points={coords} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

export function TimeSeriesChart({
  points,
  color,
  outliers,
}: {
  points: SeriesPoint[];
  color: string;
  outliers: string[];
}) {
  if (!points.length) return null;
  const outlierSet = new Set(outliers);
  const w = 640;
  const left = 44;
  const right = 626;
  const plotTop = 16;
  const plotBottom = 176;
  const plotH = plotBottom - plotTop;
  const clear = clearPoints(points, outlierSet);
  const allValues = clear.length ? clear.map((p) => p.value) : points.map((p) => p.value);
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const pad = (max - min) * 0.08 || 0.05;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin || 1;
  const xFor = (i: number) => left + (i / Math.max(points.length - 1, 1)) * (right - left);
  const yFor = (v: number) => plotBottom - ((v - yMin) / yRange) * plotH;
  const labelEvery = Math.max(1, Math.floor(points.length / 7));

  const areaPath =
    clear.length >= 2
      ? `M${clear
          .map((p) => {
            const i = points.findIndex((pt) => pt.date === p.date);
            return `${xFor(i)},${yFor(p.value)}`;
          })
          .join(' L')} L${xFor(points.findIndex((pt) => pt.date === clear[clear.length - 1].date))},${plotBottom} L${xFor(points.findIndex((pt) => pt.date === clear[0].date))},${plotBottom}Z`
      : '';

  return (
    <svg viewBox="0 0 640 210" width="100%" role="img" aria-label="Reading history" className="text-[10px] fill-[#6b7280]">
      {chartYTicks(yMin, yMax).map((v) => {
        const y = yFor(v);
        return (
          <g key={v}>
            <line x1={left} x2={right} y1={y} y2={y} stroke="#e4e8e2" />
            <text x={38} y={y + 3} textAnchor="end">
              {formatFieldHealthValue(v, v >= 10 ? 1 : 2)}
            </text>
          </g>
        );
      })}
      {areaPath ? <path d={areaPath} fill={color} opacity={0.1} /> : null}
      {clear.length >= 2 ? (
        <polyline
          points={clear
            .map((p) => {
              const i = points.findIndex((pt) => pt.date === p.date);
              return `${xFor(i)},${yFor(p.value)}`;
            })
            .join(' ')}
          fill="none"
          stroke={color}
          strokeWidth={2.6}
          strokeLinejoin="round"
        />
      ) : null}
      {points.map((p, i) => {
        const x = xFor(i);
        const isOutlier = outlierSet.has(p.date);
        const showLabel = i % labelEvery === 0 || i === points.length - 1;
        if (isOutlier) {
          return (
            <g key={p.date}>
              <circle cx={x} cy={plotBottom} r={5.5} fill="#fff" stroke="#c9822b" strokeWidth={2.5} strokeDasharray="3 2" />
              <text x={x + 9} y={plotBottom - 6} fill="#9a5b00" fontWeight={600}>
                {formatCloudOutlierValue(p.value)} · cloud?
              </text>
              {showLabel ? (
                <text x={x} y={198} textAnchor="middle">
                  {formatReportDateShort(p.date)}
                </text>
              ) : null}
            </g>
          );
        }
        const y = yFor(p.value);
        const isLatest = i === points.length - 1;
        return (
          <g key={p.date}>
            <circle cx={x} cy={y} r={4} fill="#fff" stroke={color} strokeWidth={2.4} />
            {showLabel ? (
              <text x={x} y={198} textAnchor="middle">
                {formatReportDateShort(p.date)}
              </text>
            ) : null}
            {isLatest ? <circle cx={x} cy={y} r={6.5} fill={color} /> : null}
          </g>
        );
      })}
    </svg>
  );
}

export function WeatherLineChart({ points, color }: { points: SeriesPoint[]; color: string }) {
  if (points.length < 2) return null;
  const left = 44;
  const right = 626;
  const plotTop = 12;
  const plotBottom = 160;
  const plotH = plotBottom - plotTop;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.08 || 1;
  const yMin = min - pad;
  const yMax = max + pad;
  const yRange = yMax - yMin || 1;
  const xFor = (i: number) => left + (i / (points.length - 1)) * (right - left);
  const yFor = (v: number) => plotBottom - ((v - yMin) / yRange) * plotH;
  const line = points.map((p, i) => `${xFor(i)},${yFor(p.value)}`).join(' ');
  const area = `M${line.replace(/ /g, ' L')} L${right},${plotBottom} L${left},${plotBottom}Z`;
  const labelIdx = [
    0,
    Math.floor(points.length / 6),
    Math.floor(points.length / 3),
    Math.floor(points.length / 2),
    Math.floor((points.length * 2) / 3),
    Math.floor((points.length * 5) / 6),
    points.length - 1,
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  return (
    <svg viewBox="0 0 640 190" width="100%" role="img" className="text-[10px] fill-[#6b7280]">
      {chartYTicks(yMin, yMax).map((v) => {
        const y = yFor(v);
        return (
          <g key={v}>
            <line x1={left} x2={right} y1={y} y2={y} stroke="#e4e8e2" />
            <text x={38} y={y + 3} textAnchor="end">
              {formatFieldHealthValue(v, 1)}
            </text>
          </g>
        );
      })}
      <path d={area} fill={color} opacity={0.12} />
      <polyline points={line} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" />
      {labelIdx.map((i) => (
        <text key={i} x={xFor(i)} y={180} textAnchor="middle">
          {formatReportDateShort(points[i].date)}
        </text>
      ))}
    </svg>
  );
}

export function WeatherBarChart({ points, color }: { points: SeriesPoint[]; color: string }) {
  if (!points.length) return null;
  const left = 44;
  const right = 626;
  const plotBottom = 160;
  const plotTop = 12;
  const plotH = plotBottom - plotTop;
  const max = Math.max(...points.map((p) => p.value), 1);
  const barW = Math.max(2, (right - left) / points.length - 1.5);
  const labelIdx = [
    0,
    Math.floor(points.length / 6),
    Math.floor(points.length / 3),
    Math.floor(points.length / 2),
    Math.floor((points.length * 2) / 3),
    Math.floor((points.length * 5) / 6),
    points.length - 1,
  ].filter((v, i, arr) => arr.indexOf(v) === i);

  return (
    <svg viewBox="0 0 640 190" width="100%" role="img" className="text-[10px] fill-[#6b7280]">
      {chartYTicks(0, max).map((v) => {
        const y = plotBottom - (v / max) * plotH;
        return (
          <g key={v}>
            <line x1={left} x2={right} y1={y} y2={y} stroke="#e4e8e2" />
            <text x={38} y={y + 3} textAnchor="end">
              {formatFieldHealthValue(v, 0)}
            </text>
          </g>
        );
      })}
      {points.map((p, i) => {
        const x = left + i * ((right - left) / points.length) + 1;
        const h = (p.value / max) * plotH;
        const y = plotBottom - h;
        const opacity = p.value >= max * 0.85 ? 0.95 : 0.55;
        return <rect key={p.date} x={x} y={y} width={barW} height={h} rx={1.5} fill={color} opacity={opacity} />;
      })}
      {labelIdx.map((i) => {
        const x = left + i * ((right - left) / points.length) + barW / 2;
        return (
          <text key={i} x={x} y={180} textAnchor="middle">
            {formatReportDateShort(points[i].date)}
          </text>
        );
      })}
    </svg>
  );
}
